import { Search } from '@mui/icons-material'
import { Box, Button, Card, Divider, InputBase, Stack } from '@mui/material'
import { isEmpty } from 'lodash'
import { ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { CustomList } from './CustomList'
import { useFilteredList } from './hooks/useFilteredList'
import { useTransferList } from './hooks/useTransferList'
import { TransferListContext } from './TransferListContext'
import { TransferListItem, TransferListProps } from './types'

export const TransferList = ({
  all,
  disabled,
  maxActive,
  onChange,
  value,
}: TransferListProps): JSX.Element => {
  const { t } = useTranslation('components')

  const rightList: TransferListItem[] = all.filter((transferListItem: TransferListItem): boolean =>
    value.right.some((rightValue: string): boolean => transferListItem.value === rightValue),
  )
  const leftList: TransferListItem[] = all.filter(
    (transferListItem: TransferListItem): boolean =>
      !rightList.some(
        (rightValue: TransferListItem): boolean => transferListItem.value === rightValue.value,
      ),
  )

  const { left, leftChecked, moveLeft, moveRight, right, rightChecked, ...contextProps } =
    useTransferList({ leftList, rightList })

  const { filter, filteredLeft, filteredRight, setFilter } = useFilteredList({
    leftList: left,
    rightList: right,
  })

  useEffect(() => {
    onChange({
      left: left
        .map((el: TransferListItem): TransferListItem['value'] => el.value)
        .toSorted((a: TransferListItem['value'], b: TransferListItem['value']): number =>
          a.toLowerCase().localeCompare(b.toLowerCase()),
        ),
      right: right
        .map(({ value }: TransferListItem): TransferListItem['value'] => value)
        .toSorted((a: TransferListItem['value'], b: TransferListItem['value']): number =>
          a.toLowerCase().localeCompare(b.toLowerCase()),
        ),
    })
  }, [filteredRight.length])

  const disabledByMaxActiveValue: boolean =
    maxActive !== undefined && value.right.length >= maxActive

  const disabledByMaxActiveChecked: boolean =
    maxActive !== undefined && leftChecked.length + value.right.length > maxActive

  const ButtonGroup: JSX.Element = (
    <FlexBox
      axis='xy'
      sx={{
        gap: 1,
        px: 1,
      }}
    >
      <Button
        data-cy='transfer-list-left-to-right-button'
        disabled={
          disabled || isEmpty(leftChecked) || disabledByMaxActiveValue || disabledByMaxActiveChecked
        }
        onClick={moveRight}
        variant='outlined'
      >
        {t('transferList.button.left')}
      </Button>

      <Button
        data-cy='transfer-list-right-to-left-button'
        disabled={disabled || isEmpty(rightChecked)}
        onClick={moveLeft}
        variant='outlined'
      >
        {t('transferList.button.right')}
      </Button>
    </FlexBox>
  )

  return (
    <Box sx={{ width: '900px' }}>
      <Card>
        <InputBase
          data-cy='transfer-list-filter'
          disabled={disabled}
          endAdornment={<Search data-cy='search-icon' />}
          fullWidth
          onChange={(event: ChangeEvent<HTMLInputElement>): void => setFilter(event.target.value)}
          placeholder={t('transferList.filter')}
          sx={{ p: 2 }}
          value={filter}
        />

        <Divider />

        <Stack direction='row'>
          <TransferListContext.Provider
            value={{
              ...contextProps,
              isDisabled: disabled,
            }}
          >
            <CustomList
              isDisabledMaxActive={disabledByMaxActiveValue}
              items={filteredLeft}
              title={t('transferList.title.left')}
            />

            {ButtonGroup}

            <CustomList
              items={filteredRight}
              title={t('transferList.title.right')}
            />
          </TransferListContext.Provider>
        </Stack>
      </Card>
    </Box>
  )
}
